<template>
  <div class="mb-4">
    <StoryboardInstruction
      @regenerate="$emit('regenerate')"
      @input="$emit('update:instruction', $event)"
      :value="instruction"
      :label="label"
      :loading="loading"
      :rows="rows"
      :max-height="maxHeight"
      :status="generationStatus"
      :button-icon="nodeId ? 'mdi-refresh' : 'mdi-creation'"
    />

    <div class="image-preview-container" :class="imageClass">
      <div v-if="filePreview" class="image-preview">
        <v-img :src="filePreview" alt="Generated image" class="preview-image" contain />
      </div>
      <div v-else class="image-placeholder">
        <v-icon size="48" :color="placeholderIconColor">mdi-image-outline</v-icon>
        <div class="placeholder-text">{{ placeholderText }}</div>
      </div>

      <v-btn
        @click="handleImageAction"
        :elevation="0"
        class="image-action-btn"
        color="primary"
        fab
        x-small
      >
        <v-icon>{{ nodeId ? 'mdi-delete' : 'mdi-upload' }}</v-icon>
      </v-btn>
    </div>

    <DefaultModal
      @submit="handleFileUpload"
      :title="$t('aiwf_sb_form_upload_visual_reference')"
      submit-button-text="upload"
      ref="fileUploadModal"
    >
      <template v-slot:content>
        <UploadField
          ref="uploadField"
          :rules="[v => (!!v && v.size > 0) || $t('form_rules.mandatory')]"
          :multiple="false"
          :accept="'image/*'"
        />
      </template>
    </DefaultModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import StoryboardInstruction from './StoryboardInstruction.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import UploadField from '@/components/content/default/Form/UploadField.vue'
import RenditionActions from '@/store/content/rendition/actions-types'
import AppActions from '@/store/app/actions-types'
import ContentActions from '@/store/content/actions/actions-types'
import { BgActionEventBus } from '@/bgActionEventBus'

export default {
  name: 'StoryboardImageGenerate',

  components: {
    StoryboardInstruction,
    DefaultModal,
    UploadField
  },

  data() {
    return {
      filePreview: null
    }
  },

  props: {
    instruction: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    nodeId: {
      type: [Number, String],
    },
    rows: {
      type: [Number, String],
      default: 3
    },
    maxHeight: {
      type: String,
      default: '80px'
    },
    parentId: {
      type: [Number, String],
      default: null
    },
    storyboardData: {
      type: Object,
      default: () => ({})
    },
    sceneIndex: {
      type: Number,
      default: 0
    },
    generationStatus: {
      type: String,
      default: ''
    },
  },

  mounted() {
    if (this.nodeId) {
      this.getFilePreview(this.nodeId)
      this.listenForRendition()
    }
  },

  watch: {
    nodeId(newNodeId) {
      if (newNodeId) {
        this.getFilePreview(newNodeId)
        this.listenForRendition()
      }
    }
  },

  computed: {
    imageClass() {
      return this.nodeId ? 'image-active' : 'image-inactive'
    },

    imagePreview() {
      return this.filePreview
    },

    placeholderIconColor() {
      return this.nodeId ? '#4caf50' : '#bdbdbd'
    },

    placeholderText() {
      if (this.loading) return this.$t('ai_workforce.storyboard.generating_image')
      if (this.nodeId) return this.$t('ai_workforce.storyboard.image_ready')
      return this.$t('ai_workforce.storyboard.no_image_generated')
    },

    generateButtonColor() {
      if (this.loading) return 'primary'
      if (this.nodeId) return 'success'
      return 'primary'
    },

    generateButtonDisabled() {
      return this.loading || !this.instruction.trim()
    },

    generateIcon() {
      if (this.loading) return 'mdi-loading'
      if (this.nodeId) return 'mdi-refresh'
      return 'mdi-image-plus'
    }
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_INFO_MESSAGE,
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    ...mapActions('content/actions', [
      ContentActions.UPLOAD_FILE,
    ]),

    ...mapActions('content/rendition', [
      RenditionActions.GET_PREVIEW,
    ]),

    handleNotification(message) {
      if (message.type == 'rendition_service' && message.status == 'done' && message.node_id == this.nodeId) {
        this.getFilePreview(this.nodeId)
        BgActionEventBus.$off('bgAction', this.handleNotification)
      }
    },

    listenForRendition() {
      BgActionEventBus.$off('bgAction', this.handleNotification)
      BgActionEventBus.$on('bgAction', this.handleNotification)
    },

    async getFilePreview(nodeId) {
      const result = await this[RenditionActions.GET_PREVIEW](nodeId)
      this.filePreview = result[0].url || null
    },

    handleImageAction() {
      if (this.nodeId) {
        this.deleteImage()
      } else {
        this.uploadImage()
      }
    },

    uploadImage() {
      this.$refs.fileUploadModal.openModal()
    },

    handleFileUpload() {
      const file = this.$refs.uploadField.getFiles()

      if (file) {
        const reader = new FileReader()
        reader.onload = (imageData) => {
          // get the parent ID from the storyboard data
          this[AppActions.OPEN_APP_INFO_MESSAGE](this.$t('upload_started'))
          this[ContentActions.UPLOAD_FILE]({parentId: this.parentId, file})
            .then((response) => {
              this.filePreview = imageData.target.result

              this.handleFileuploadKeyframe(response)

              this[AppActions.OPEN_APP_SUCCESS_MESSAGE]('aiwf_sb_form_image_uploaded')
            })
            .catch((error) => {
              console.error('Error uploading image:', error)
              this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
            }
          )
        }
        reader.readAsDataURL(file)
      }

      this.$refs.fileUploadModal.closeModal()
      this.$refs.fileUploadModal.resetForm()
    },

    deleteImage() {
      this.filePreview = null
      this.$emit('delete')
    },

    handleFileuploadKeyframe(response) {
      this.$emit('uploaded', {nodeId: response.nodeId, sceneIndex: this.sceneIndex})
    },
  },

  beforeDestroy() {
    BgActionEventBus.$off('bgAction', this.handleNotification)
  },

  emits: ['update:instruction', 'regenerate', 'upload', 'delete', 'uploaded', 'send-message']
}
</script>

<style scoped>
  .image-preview-container {
    height: 180px;
    border-radius: 8px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed #e0e0e0;
    transition: all 0.3s ease;
  }

  .image-active {
    background-color: #f8f9fa;
    border-color: #4caf50;
  }

  .image-inactive {
    background-color: #fafafa;
    border-color: #e0e0e0;
  }

  .image-preview {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .preview-image {
    max-width: 100%;
    max-height: 100%;
    border-radius: 4px;
  }

  .image-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 16px;
  }

  .placeholder-text {
    margin-top: 8px;
    font-size: 12px;
    color: #757575;
    font-weight: 500;
  }

  .image-action-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
  }
</style>
